import { observer } from "mobx-react";
import { NumberQuestion } from "./commonComponents";
import { Box, Grid, GridItem, VBox } from "@/components-v2/Box";
import Big from "big.js";
import { CURRENCY_NOK } from "@/utils/currency";
import { useLocaleFormat, useTranslation } from "@/stores/RootStoreContext";
import { NumberFieldModel } from "@/common/commonForms";
import { InfoButton } from "@/components-v2/InfoButton";
import { MTextInput } from "@/components-v2/TextInput";

export const IncreaseNominalValue = observer(function ({
  increaseInNominalValue,
  currentNominalValue,
  currentNumberOfShares,
  isProjectLocked,
}: {
  increaseInNominalValue: NumberFieldModel;
  currentNominalValue: number;
  currentNumberOfShares: number;
  isProjectLocked: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <NumberQuestion
        question={t(
          "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.nominalIncrease",
        )}
        prefix={CURRENCY_NOK}
        field={increaseInNominalValue}
        isLocked={isProjectLocked}
      />
      <VBox gap={"md"}>
        <div>
          {t("decisions.increaseShareCapitalAgainstCash.increaseNominalValue.nominalTo")}
          <InfoButton
            infoText={t(
              "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.capitalIncrease-infoText",
            )}
          />
        </div>
        <DescriptionTable
          increaseInNominalValue={increaseInNominalValue}
          currentNominalValue={currentNominalValue}
          currentNumberOfShares={currentNumberOfShares}
        />
      </VBox>
    </>
  );
});

export const IncreaseNominalValue2 = observer(function ({
  increaseInNominalValue,
  currentNominalValue,
  currentNumberOfShares,
  isProjectLocked,
}: {
  increaseInNominalValue: NumberFieldModel;
  currentNominalValue: number;
  currentNumberOfShares: number;
  isProjectLocked: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <MTextInput
        prefix={CURRENCY_NOK}
        label={t(
          "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.nominalIncrease.short",
        )}
        model={increaseInNominalValue}
        disabled={isProjectLocked}
      />
      <DescriptionTable
        increaseInNominalValue={increaseInNominalValue}
        currentNominalValue={currentNominalValue}
        currentNumberOfShares={currentNumberOfShares}
      />
    </>
  );
});

const DescriptionTable = observer(function ({
  increaseInNominalValue,
  currentNominalValue,
  currentNumberOfShares,
}: {
  increaseInNominalValue: NumberFieldModel;
  currentNominalValue: number;
  currentNumberOfShares: number;
}) {
  const { t } = useTranslation();
  const { formatNumber } = useLocaleFormat();

  return (
    <Grid cols={4}>
      <GridItem colSpan={4} className={"font-bold"}>
        <Grid cols={4}>
          <GridItem colSpan={2}>
            {t("decisions.increaseShareCapitalAgainstCash.increaseNominalValue.keyFigures")}
          </GridItem>
          <GridItem justifySelf={"end"}>{t("before")}</GridItem>
          <GridItem justifySelf={"end"}>{t("after")}</GridItem>
        </Grid>
      </GridItem>
      <GridItem colSpan={2}>{t("transactions.issueTransaction.nominal-amount")}</GridItem>
      <GridItem justifySelf={"end"}>{formatNumber(currentNominalValue)}</GridItem>
      <GridItem justifySelf={"end"}>
        {formatNumber(
          Big(currentNominalValue)
            .plus(increaseInNominalValue.toNumber() || 0)
            .toNumber(),
        )}
      </GridItem>
      <GridItem colSpan={2}>
        <Box>{t("decisions.increaseShareCapitalAgainstCash.numberOfShares")}</Box>
      </GridItem>
      <GridItem justifySelf={"end"}>{formatNumber(currentNumberOfShares)}</GridItem>
      <GridItem justifySelf={"end"}>{formatNumber(currentNumberOfShares)}</GridItem>
      <GridItem colSpan={2}>
        <Box>{t("shareClasses.capital")}</Box>
      </GridItem>
      <GridItem justifySelf={"end"}>
        {formatNumber(Big(currentNominalValue).times(currentNumberOfShares).toNumber())}
      </GridItem>
      <GridItem justifySelf={"end"}>
        {formatNumber(
          Big(currentNominalValue)
            .plus(increaseInNominalValue.toNumber() || 0)
            .times(currentNumberOfShares)
            .toNumber() || 0,
        )}
      </GridItem>
      <GridItem colSpan={2}>
        <Box>
          {t("decisions.increaseShareCapitalAgainstCash.increaseNominalValue.capitalIncrease")}
        </Box>
      </GridItem>
      <GridItem justifySelf={"end"} />
      <GridItem justifySelf={"end"}>
        {formatNumber(
          Big(increaseInNominalValue.toNumber() || 0)
            .times(currentNumberOfShares)
            .toNumber() || 0,
        )}
      </GridItem>
    </Grid>
  );
});
